import { TooltipV2 } from '@cimplico/components';

import {
    NoSymbolIcon,
    CheckIcon,
    PaperClipIcon,
    PencilIcon,
    PlusIcon,
    SignalIcon,
    TrashIcon,
    XMarkIcon,
    UserPlusIcon,
    UserMinusIcon,
    PencilSquareIcon,
    PaperAirplaneIcon,
    ArchiveBoxIcon,
    CurrencyDollarIcon,
    ArrowUturnLeftIcon,
    CalculatorIcon,
    LinkIcon,
} from '@heroicons/react/24/outline';
import { AccountHistoryEventEnum } from '../../../types/account';
import { WorksheetHistoryEventEnum, WorksheetHistoryResult } from '../../../types/worksheet';
import { calcRelativeTime, formatDateTime } from '../../../utilities/formatting';

type WorksheetHistoryRowProps = {
    record: WorksheetHistoryResult;
};

function WorksheetHistoryRow({ record }: WorksheetHistoryRowProps): JSX.Element {
    return (
        <div className="flex items-center gap-2">
            <span className="shrink-0">
                <HistoryIcon type={record.eventType} />
            </span>
            <div className="flex items-center gap-x-2 flex-wrap">
                <p className="text-sm text-gray-600">
                    {record.message}
                    <span>
                        {' by'}{' '}
                        <span className="font-medium text-gray-900">{`${
                            record.createdBy ? `${record.createdBy.firstName} ${record.createdBy.lastName}` : 'system'
                        }`}</span>
                    </span>
                </p>
                <div>
                    <TooltipV2 message={formatDateTime(record.createdAt)}>
                        <p className="text-xs text-gray-600">{calcRelativeTime(record.createdAt)}</p>
                    </TooltipV2>
                </div>
            </div>
        </div>
    );
}

type HistoryIconProps = {
    type: WorksheetHistoryEventEnum | AccountHistoryEventEnum;
};
function HistoryIcon({ type }: HistoryIconProps): JSX.Element {
    switch (type) {
        case WorksheetHistoryEventEnum.WORKSHEET_LINK_PRIOR:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center bg-blue-500">
                    <LinkIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.WORKSHEET_CREATE:
        case AccountHistoryEventEnum.ACCOUNT_CREATED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-green-500">
                    <PlusIcon className="h-3 w-3 text-white" />
                </span>
            );

        case WorksheetHistoryEventEnum.WORKSHEET_RESET:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center bg-red-500">
                    <ArrowUturnLeftIcon className="h-3 w-3 text-white" />
                </span>
            );

        case WorksheetHistoryEventEnum.CHECKLIST_ITEM_CHECK:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center bg-green-500">
                    <CheckIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.CHECKLIST_ITEM_UNCHECK:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-yellow-500">
                    <XMarkIcon className="h-3 w-3 text-white" />
                </span>
            );

        case WorksheetHistoryEventEnum.CHECKLIST_ITEM_NOT_APPLICABLE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-gray-500">
                    <NoSymbolIcon className="h-3 w-3 text-white" />
                </span>
            );

        case WorksheetHistoryEventEnum.STATUS_CHANGE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-indigo-500">
                    <SignalIcon className="h-3 w-3 text-white" />
                </span>
            );

        case WorksheetHistoryEventEnum.ENTRY_CREATE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-green-500">
                    <PlusIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.ENTRY_DELETE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-red-500">
                    <TrashIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.ENTRY_UPDATE:
        case WorksheetHistoryEventEnum.WORKSHEET_FIELD_CHANGE:
        case WorksheetHistoryEventEnum.WORKSHEET_DETAILS_UPDATE:
        case WorksheetHistoryEventEnum.WORKSHEET_TYPE_CHANGE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-blue-500">
                    <PencilIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.ENTRY_ATTACHMENT_UPDATE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-blue-500">
                    <PaperClipIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.ASSIGNEE_ADD:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-green-500">
                    <UserPlusIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.ASSIGNEE_REMOVE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-red-500">
                    <UserMinusIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.WORKSHEET_ARCHIVED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-red-500">
                    <ArchiveBoxIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.WORKSHEET_UNARCHIVED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-green-500">
                    <PlusIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.DRAFT_JOURNAL_CREATE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-indigo-500">
                    <PencilSquareIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.JOURNAL_CREATE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-indigo-500">
                    <PlusIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.JOURNAL_POSTED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-green-500">
                    <PaperAirplaneIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.JOURNAL_REMOVE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-red-500">
                    <TrashIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.JOURNAL_UPDATE:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-blue-500">
                    <PencilIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.JOURNAL_VOIDED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-red-500">
                    <XMarkIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.JOURNAL_ARCHIVED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-gray-500">
                    <ArchiveBoxIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.BALANCE_CHANGED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-yellow-500">
                    <CurrencyDollarIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.CALCULATION_ADDED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-green-500">
                    <CalculatorIcon className="h-3 w-3 text-white" />
                </span>
            );
        case WorksheetHistoryEventEnum.CALCULATION_REMOVED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-red-500">
                    <CalculatorIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.ACCOUNT_ARCHIVED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-red-500">
                    <ArchiveBoxIcon className="h-3 w-3 text-white" />
                </span>
            );
        case AccountHistoryEventEnum.ACCOUNT_UNARCHIVED:
            return (
                <span className="h-5 w-5 rounded-full flex items-center justify-center  bg-green-500">
                    <PlusIcon className="h-3 w-3 text-white" />
                </span>
            );
        default:
            return <></>;
    }
}

export default WorksheetHistoryRow;
